"use client";

import { useState } from "react";
import Link from "next/link";
import { format } from "date-fns";
import { useTranslations } from "next-intl";
import {
  Star,
  MoreVertical,
  Pencil,
  Trash2,
  Flag,
  ShieldOff,
} from "lucide-react";
import { OptimizedImage } from "@/components/ui/optimized-image";
import { ImageModal } from "@/components/ImageModal";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { ResponsiveModal } from "@/components/ui/responsive-modal";
import { Button } from "@/components/ui/button";
import { cn } from "@/lib/utils";
import { LANGUAGES } from "@/i18n/languages";
import "flag-icons/css/flag-icons.min.css";
import { ReviewReportSheet } from "./ReviewReportSheet";
import { UserBlockConfirm } from "./UserBlockConfirm";
import { deleteReviewAction } from "@/app/actions/reviews";

// Body text bodies longer than this get a more/less toggle.
const SHOW_MORE_THRESHOLD = 120;

export interface ReviewCardProps {
  id: string;
  rating: number;
  comment: string | null;
  translatedComment?: string | null;
  sourceLang?: string;
  createdAt: string;
  /** Already-masked reviewer name to display. Pass undefined to hide
   *  the name row entirely (e.g. when the viewer is the author). */
  reviewerName?: string;
  productName?: string;
  images?: string[];
  selectedTags?: string[];
  /** The review author's user id. Combined with currentUserId, the card
   *  decides whether to render owner actions (edit/delete) or moderation
   *  actions (report/block) — or neither. */
  authorUserId?: string;
  currentUserId?: string;
  /** If provided, the whole card becomes a Link to this href. Interactive
   *  children (menus, more-toggle, thumbnails) stop propagation so they
   *  don't trigger navigation. */
  href?: string;
  /** Hide the body bottom actions entirely (used when a wrapping page
   *  provides its own actions header — e.g. /profile/reviews). */
  hideActions?: boolean;
  /** "compact" renders photos as a single 80px thumbnail beside the body
   *  (with a +N overlay) instead of the bottom strip — used in the product
   *  detail slider where card heights must stay uniform.
   *  "plain" drops the gray card chrome so a wrapping list can render a
   *  flat divided list — used on the full reviews page. */
  variant?: "default" | "compact" | "plain";
  /** Partner reply shown as a nested block under the review body.
   *  translatedReply (viewer-locale translation joined from
   *  review_translations) wins over the raw replyContent when present;
   *  replyLang is the language the partner wrote the reply in. */
  replyContent?: string | null;
  replyAt?: string | null;
  replyLang?: string | null;
  translatedReply?: string | null;
  onEdit?: () => void;
  onDeleted?: () => void;
  onAuthorBlocked?: (userId: string) => void;
}

export default function ReviewCard({
  id,
  rating,
  comment,
  translatedComment,
  sourceLang,
  createdAt,
  reviewerName,
  productName,
  images,
  selectedTags,
  authorUserId,
  currentUserId,
  href,
  hideActions,
  variant = "default",
  replyContent,
  replyAt,
  replyLang,
  translatedReply,
  onEdit,
  onDeleted,
  onAuthorBlocked,
}: ReviewCardProps) {
  const t = useTranslations("reviews");
  const [expanded, setExpanded] = useState(false);
  const [replyExpanded, setReplyExpanded] = useState(false);
  const [modalOpen, setModalOpen] = useState(false);
  const [modalIndex, setModalIndex] = useState(0);
  const [showDeleteDialog, setShowDeleteDialog] = useState(false);
  const [isDeleting, setIsDeleting] = useState(false);
  const [showReportSheet, setShowReportSheet] = useState(false);
  const [showBlockConfirm, setShowBlockConfirm] = useState(false);

  const isOwn = !!authorUserId && authorUserId === currentUserId;
  const canModerate = !!currentUserId && !!authorUserId && !isOwn;
  const displayComment = translatedComment || comment || "";
  const isLong = displayComment.length > SHOW_MORE_THRESHOLD;
  const primaryTag = selectedTags?.[0];
  const isCompact = variant === "compact";
  const hasImages = !!images && images.length > 0;
  // Side thumbnail (compact) instead of the bottom photo strip.
  const sideThumb = isCompact && hasImages;
  // Source-language flag beside the name, only when the body is translated
  // (paired with the "translated" label so the flag never carries the
  // meaning alone).
  const translatedFlag = translatedComment
    ? LANGUAGES.find((l) => l.code === (sourceLang || "ko"))?.flagCode
    : undefined;
  const showNameRow = !isOwn && !!reviewerName;
  const displayReply = translatedReply || replyContent || "";
  const isReplyLong = displayReply.length > SHOW_MORE_THRESHOLD;
  const replyFlag = translatedReply
    ? LANGUAGES.find((l) => l.code === (replyLang || "ko"))?.flagCode
    : undefined;

  const showOwnerMenu = !hideActions && isOwn && (!!onEdit || !!onDeleted);
  const showModerationMenu = !hideActions && canModerate;

  const modalImages = (images || []).map((url, idx) => ({
    url,
    name: `${t("image")} ${idx + 1}`,
  }));

  const handleDelete = async () => {
    setIsDeleting(true);
    try {
      const result = await deleteReviewAction(id);
      if (result.success) onDeleted?.();
    } finally {
      setIsDeleting(false);
      setShowDeleteDialog(false);
    }
  };

  const cardBody = (
    <div
      className={cn(
        variant === "plain" ? "py-5" : "bg-gray-50 rounded-2xl p-4",
      )}
    >
      {/* Row 1: stars + primary tag | date + menu */}
      <div className="flex items-center justify-between gap-2 mb-2">
        <div className="flex items-center gap-2 min-w-0">
          <div className="flex items-center gap-0.5 shrink-0">
            {[1, 2, 3, 4, 5].map((star) => (
              <Star
                key={star}
                className={cn(
                  "w-4 h-4",
                  star <= rating
                    ? "text-orange-400 fill-orange-400"
                    : "text-gray-300",
                )}
              />
            ))}
          </div>
          {primaryTag && (
            <span className="inline-flex items-center gap-1 bg-orange-50 text-orange-600 text-xs px-2 py-0.5 rounded-md truncate">
              <span className="inline-block w-1.5 h-1.5 bg-orange-400 rounded-sm shrink-0" />
              <span className="truncate">{t(primaryTag)}</span>
            </span>
          )}
        </div>
        <div className="flex items-center gap-1 shrink-0">
          <span className="text-xs text-gray-400">
            {format(new Date(createdAt), "yyyy.MM.dd")}
          </span>
          {showOwnerMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                onClick={(e) => e.stopPropagation()}
                className="p-1 -mr-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
              >
                <MoreVertical className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                {onEdit && (
                  <DropdownMenuItem
                    onClick={(e) => {
                      e.stopPropagation();
                      onEdit();
                    }}
                  >
                    <Pencil className="w-4 h-4 mr-2" />
                    {t("editReview")}
                  </DropdownMenuItem>
                )}
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowDeleteDialog(true);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <Trash2 className="w-4 h-4 mr-2" />
                  {t("deleteReview")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
          {showModerationMenu && (
            <DropdownMenu>
              <DropdownMenuTrigger
                onClick={(e) => e.stopPropagation()}
                aria-label={t("actions.report")}
                className="p-1 -mr-1 rounded-full hover:bg-gray-200 transition-colors text-gray-500"
              >
                <MoreVertical className="w-4 h-4" />
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end">
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowReportSheet(true);
                  }}
                >
                  <Flag className="w-4 h-4 mr-2" />
                  {t("actions.report")}
                </DropdownMenuItem>
                <DropdownMenuItem
                  onClick={(e) => {
                    e.stopPropagation();
                    setShowBlockConfirm(true);
                  }}
                  className="text-red-600 focus:text-red-600"
                >
                  <ShieldOff className="w-4 h-4 mr-2" />
                  {t("actions.block")}
                </DropdownMenuItem>
              </DropdownMenuContent>
            </DropdownMenu>
          )}
        </div>
      </div>

      {/* Reviewer meta: source-language flag + name + translated badge.
          The row also renders name-less when only the badge applies. */}
      {(showNameRow || translatedFlag) && (
        <p className="flex items-center gap-1 text-xs text-gray-500 mb-2 min-w-0">
          {translatedFlag && (
            <span
              className={`fi fi-${translatedFlag} w-4! h-3 bg-cover! rounded-[2px] border border-gray-200 shrink-0`}
              aria-hidden
            />
          )}
          {showNameRow && <span className="truncate">{reviewerName}</span>}
          {translatedComment && (
            <span className="text-gray-400 shrink-0">· {t("translated")}</span>
          )}
        </p>
      )}

      {/* Product name */}
      {productName && (
        <p className="text-xs text-gray-400 mb-2">{productName}</p>
      )}

      {/* Body with optional more-toggle. Compact puts a single thumbnail
          beside the text, clamped so the text ends at the thumbnail height. */}
      {(displayComment || sideThumb) && (
        <div className={cn(sideThumb && "flex items-start gap-3")}>
          {displayComment && (
            <div className={cn(sideThumb && "flex-1 min-w-0")}>
              <p
                className={cn(
                  "text-sm text-gray-800 leading-relaxed wrap-break-word",
                  !expanded && (sideThumb ? "line-clamp-3" : "line-clamp-4"),
                )}
              >
                {displayComment}
              </p>
              {/* Compact cards skip the toggle — the whole card links to
                  the full reviews page where the text is readable. */}
              {isLong && !isCompact && (
                <button
                  type="button"
                  onClick={(e) => {
                    e.stopPropagation();
                    e.preventDefault();
                    setExpanded((v) => !v);
                  }}
                  className="text-sm text-gray-500 hover:text-gray-700 mt-1"
                >
                  {expanded ? `${t("showLess")} ↑` : `${t("showMore")} ↓`}
                </button>
              )}
            </div>
          )}
          {sideThumb && (
            // Not a modal trigger: the tap bubbles to the card Link so it
            // navigates to the full reviews page like the rest of the card.
            <span className="relative block w-20 h-20 shrink-0 rounded-lg overflow-hidden">
              <OptimizedImage
                src={images![0]}
                alt={`${t("image")} 1`}
                fill
                className="object-cover"
                sizes="80px"
              />
              <span className="absolute bottom-1 right-1 z-10 rounded-md bg-black/40 backdrop-blur-sm px-1.5 py-0.5 text-[10px] font-medium text-white">
                {images!.length}
              </span>
            </span>
          )}
        </div>
      )}

      {/* Photo strip (compact shows the side thumbnail instead) */}
      {!isCompact && images && images.length > 0 && (
        <div className="flex gap-2 mt-3 overflow-x-auto scrollbar-hide">
          {images.map((url, idx) => (
            <button
              key={url}
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setModalIndex(idx);
                setModalOpen(true);
              }}
              className="relative w-20 h-20 shrink-0 rounded-lg overflow-hidden hover:opacity-90 transition-opacity"
              aria-label={`${t("image")} ${idx + 1}`}
            >
              <OptimizedImage
                src={url}
                alt={`${t("image")} ${idx + 1}`}
                fill
                className="object-cover"
                sizes="80px"
              />
            </button>
          ))}
        </div>
      )}

      {/* Partner reply */}
      {displayReply && (
        <div className="mt-3 bg-gray-100 rounded-xl p-3">
          <div className="flex items-center justify-between gap-2 mb-1">
            <span className="flex items-center gap-1 text-xs font-medium text-gray-700 min-w-0">
              {t("partnerReply")}
              {replyFlag && (
                <>
                  <span
                    className={`fi fi-${replyFlag} w-4! h-3 bg-cover! rounded-[2px] border border-gray-200 shrink-0`}
                    aria-hidden
                  />
                  <span className="font-normal text-gray-400 shrink-0">
                    · {t("translated")}
                  </span>
                </>
              )}
            </span>
            {replyAt && (
              <span className="text-xs text-gray-400 shrink-0">
                {format(new Date(replyAt), "yyyy.MM.dd")}
              </span>
            )}
          </div>
          <p
            className={cn(
              "text-sm text-gray-800 leading-relaxed whitespace-pre-wrap wrap-break-word",
              !replyExpanded && "line-clamp-4",
            )}
          >
            {displayReply}
          </p>
          {isReplyLong && (
            <button
              type="button"
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                setReplyExpanded((v) => !v);
              }}
              className="text-sm text-gray-500 hover:text-gray-700 mt-1"
            >
              {replyExpanded ? `${t("showLess")} ↑` : `${t("showMore")} ↓`}
            </button>
          )}
        </div>
      )}
    </div>
  );

  return (
    <>
      {href ? (
        <Link href={href} className="block">
          {cardBody}
        </Link>
      ) : (
        cardBody
      )}

      <ImageModal
        images={modalImages}
        currentIndex={modalIndex}
        isOpen={modalOpen}
        onClose={() => setModalOpen(false)}
        onIndexChange={setModalIndex}
      />

      {showOwnerMenu && (
        <ResponsiveModal
          open={showDeleteDialog}
          onOpenChange={setShowDeleteDialog}
          title={t("confirmDeleteTitle")}
          description={t("confirmDelete")}
          footer={
            <div className="flex gap-2">
              <Button
                variant="outline"
                className="flex-1"
                onClick={() => setShowDeleteDialog(false)}
                disabled={isDeleting}
              >
                {t("cancel")}
              </Button>
              <Button
                variant="destructive"
                className="flex-1"
                onClick={handleDelete}
                disabled={isDeleting}
              >
                {isDeleting ? t("deleting") : t("delete")}
              </Button>
            </div>
          }
        />
      )}

      {showModerationMenu && (
        <>
          <ReviewReportSheet
            reviewId={id}
            open={showReportSheet}
            onOpenChange={setShowReportSheet}
          />
          <UserBlockConfirm
            blockedUserId={authorUserId!}
            blockedUserName={reviewerName || t("anonymous")}
            open={showBlockConfirm}
            onOpenChange={setShowBlockConfirm}
            onBlocked={onAuthorBlocked}
          />
        </>
      )}
    </>
  );
}
